import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, Loader } from "@/components/common";
import { formatINR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Crown, Check, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "Unlimited invoices, inventory & customers",
  "AI Business Advisor (Claude) & smart alerts",
  "Cash flow, forecasting & marketing analytics",
  "Multi-branch, team roles & audit log",
  "Professional reports & CSV export",
];

function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const s = document.createElement("script");
    s.src = src; s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Subscription() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/subscription/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);
  if (!status) return <Loader label="Loading your plan…" />;

  const amount = (status.amount || 299900) / 100;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/create-order");
      const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!ok) { toast.error("Couldn't load Razorpay. Check your connection."); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "KHEVORA OS", description: "KHEVORA Pro — Monthly",
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#6366F1" },
        handler: async (resp) => {
          try {
            await api.post("/subscription/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("You're on KHEVORA Pro! 🎉");
            load();
          } catch { toast.error("Payment verification failed."); }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Subscription" subtitle="Your KHEVORA Pro plan & billing." />

      {status.active && (
        <div className="mb-6 rounded-xl border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-4 flex items-center gap-3 animate-fade-up" data-testid="sub-active">
          <ShieldCheck className="h-5 w-5 text-[hsl(var(--success))]" />
          <div><div className="font-semibold text-[hsl(var(--success))]">KHEVORA Pro is active</div>
            <div className="text-sm text-muted-foreground">Renews on {formatDate(status.current_period_end)}</div></div>
        </div>
      )}

      <div className="max-w-md">
        <SectionCard testid="plan-card" className="border-primary/40">
          <div className="flex items-center gap-2 text-primary font-semibold"><Crown className="h-5 w-5" /> KHEVORA Pro</div>
          <div className="mt-3 flex items-end gap-1">
            <span className="font-heading text-4xl font-extrabold">{formatINR(amount)}</span>
            <span className="text-muted-foreground mb-1">/ month</span>
          </div>
          <div className="mt-5 space-y-2.5">
            {PERKS.map((p) => (
              <div key={p} className="flex items-start gap-2.5 text-sm">
                <Check className="h-4 w-4 text-[hsl(var(--success))] mt-0.5 shrink-0" /> {p}
              </div>
            ))}
          </div>
          <Button onClick={subscribe} disabled={busy || status.active} data-testid="subscribe-btn" className="w-full mt-6 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : status.active ? "Current Plan" : `Subscribe · ${formatINR(amount)}/mo`}
          </Button>
          {!status.configured && (
            <p className="text-xs text-muted-foreground mt-3 text-center" data-testid="sub-not-configured">
              Payments will activate once Razorpay keys are added.
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
